/**
 * CBD HUT — predictive-search.js (Refreshed + conflict-proof)
 *
 * Patch notes (sanity):
 * - closeResults() no longer removes `results` on a normal close (keeps “ready but closed” behaviour)
 * - Cleanup on disconnect: abort in-flight + clear timers
 * - Query predictive-search instances dynamically when sharing cache (avoid stale NodeList in theme editor)
 * - Header-interactions.js is the single surface coordinator (no duplicate click-away logic here)
 */

class PredictiveSearch extends SearchForm {
  constructor() {
    super();

    // If SearchForm didn't wire up input, bail safely
    if (!this.input || !this.input.form) return;

    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');

    this.isOpen = false;
    this.abortController = null;
    this.searchTerm = '';

    // Request sequencing to prevent stale results winning
    this._requestSeq = 0;

    // Tuning
    this.debounceDelay = 180;
    this._debounceTimer = null;

    // Focus timers
    this._focusOutTimer = null;

    // Pointer guard: prevent focusout-close from cancelling result clicks
    this._pointerDownInsideUntil = 0;

    // Elements
    this.resetButton = this.querySelector('button[type="reset"]');

    // Empty-state (“conversion mode”): popular + recent searches (no network)
    this._initialPanelMarkup = this.predictiveSearchResults ? this.predictiveSearchResults.innerHTML : '';
    this._recentKey = '__cbh_recent_searches__';
    this._recentMax = 6;
    this._popularFallback = [
      'CBD oil',
      'CBD gummies',
      'CBD capsules',
      'CBD vape',
      'Broad spectrum',
      'Full spectrum',
    ];

    this.setupEventListeners();
    this.toggleResetButton();

    // Defensive: if a previous version left inline styles, clear them (header CSS should own layout)
    this.clearInlinePositioning();
  }

  // Dynamic instance list (avoid stale NodeList after header re-render)
  getAllInstances() {
    try {
      return Array.from(document.querySelectorAll('predictive-search'));
    } catch (e) {
      return [this];
    }
  }

  setupEventListeners() {
    if (super.setupEventListeners) super.setupEventListeners();

    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.form.addEventListener('reset', this.onFormReset.bind(this));

    this.input.addEventListener('input', this.onChange.bind(this));
    this.input.addEventListener('change', this.onChange.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('click', this.onFocus.bind(this));

    // Focus/keyboard handlers
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // Capture pointerdown inside the component so focusout timers don't close mid-click
    this.addEventListener('pointerdown', this.onPointerDown.bind(this), true);

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.addEventListener('click', this.onResultsClick.bind(this));
    }
  }

  disconnectedCallback() {
    // Cleanup in case header is re-rendered (design mode / dynamic sections)
    try {
      if (this._debounceTimer) window.clearTimeout(this._debounceTimer);
      if (this._focusOutTimer) window.clearTimeout(this._focusOutTimer);
      this.abortInFlight();
    } catch (e) {}
  }

  isHeaderScoped() {
    try {
      return !!this.closest('.section-header');
    } catch (e) {
      return false;
    }
  }

  clearInlinePositioning() {
    const el = this.predictiveSearchResults;
    if (!el || !el.style) return;
    if (!this.isHeaderScoped()) return;

    try {
      el.style.position = '';
      el.style.left = '';
      el.style.right = '';
      el.style.top = '';
      el.style.width = '';
      el.style.margin = '';
      el.style.maxHeight = '';
      el.style.zIndex = '';
    } catch (e) {}
  }

  getQuery() {
    return this.input.value.trim();
  }

  normaliseTerm(term) {
    return (term || '').trim().replace(/\s+/g, ' ').toLowerCase();
  }

  getCacheKey(term) {
    return this.normaliseTerm(term).replace(/\s/g, '-');
  }

  isEventInside(e) {
    const t = e?.target;
    if (!t || t.nodeType !== 1) return false;

    try {
      if (this.contains(t)) return true;
    } catch (err) {}

    try {
      if (
        this.predictiveSearchResults &&
        (t === this.predictiveSearchResults || this.predictiveSearchResults.contains(t))
      )
        return true;
    } catch (err) {}

    return false;
  }

  toggleResetButton() {
    if (!this.resetButton) return;
    this.resetButton.classList.toggle('hidden', !this.getQuery().length);
  }

  getSelectableOptions() {
    return Array.from(
      this.querySelectorAll(
        'li[role="option"], .predictive-search__list-item, a.predictive-search__item, button.predictive-search__item'
      )
    ).filter((el) => el && el.offsetParent !== null);
  }

  onChange() {
    if (super.onChange) super.onChange();

    const newSearchTerm = this.getQuery();
    this.toggleResetButton();

    // Stale UI protection
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (this._debounceTimer) window.clearTimeout(this._debounceTimer);

    if (!this.searchTerm.length) {
      this.abortInFlight();
      this.removeAttribute('results');

      // If the user cleared while still focused, show popular/recent chips instead of “nothing”.
      if (document.activeElement === this.input) {
        this.renderEmptyState();
        this.open();
      } else {
        this.close(true);
      }
      return;
    }

    // If we were showing the empty state, restore the panel so the loading spinner can show.
    this.ensurePanelHasSpinner();

    // Keep the panel open while typing so results appear without a second click
    if (document.activeElement === this.input) this.open();

    const key = this.getCacheKey(this.searchTerm);
    if (this.cachedResults[key]) {
      this.renderSearchResults(this.cachedResults[key], { fromCache: true });
      return;
    }

    this._debounceTimer = window.setTimeout(() => {
      this.getSearchResults(this.searchTerm);
    }, this.debounceDelay);
  }

  onFormSubmit(event) {
    const term = this.getQuery();
    if (term.length) this.storeRecentTerm(term);

    if (!term.length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    if (super.onFormReset) super.onFormReset(event);

    const shouldReset = super.shouldResetForm ? super.shouldResetForm() : true;
    if (!shouldReset) return;

    this.searchTerm = '';
    if (this._debounceTimer) window.clearTimeout(this._debounceTimer);

    this.abortInFlight();
    this.close(true);
    this.toggleResetButton();
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    this.toggleResetButton();

    if (!currentSearchTerm.length) {
      this.renderEmptyState();
      this.open();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
      return;
    }

    // If results exist, open; else fetch
    if (this.hasAttribute('results')) {
      this.open();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onPointerDown(event) {
    // Clicking inside results can blur the input before the click fires.
    // Keep the panel open briefly so the click/navigation can complete.
    if (this.isEventInside(event)) {
      this._pointerDownInsideUntil = Date.now() + 600;
    }
  }

  onFocusOut() {
    if (this._focusOutTimer) clearTimeout(this._focusOutTimer);

    this._focusOutTimer = setTimeout(() => {
      // Guard: don't close while the user is clicking inside results (prevents cancelling clicks)
      if (Date.now() < (this._pointerDownInsideUntil || 0)) return;

      // If focus is still within this predictive-search (input or results), do nothing
      const ae = document.activeElement;
      if (ae && (this.contains(ae) || this.isEventInside({ target: ae }))) return;

      // Let header-interactions handle global “outside header” logic;
      // here we only close this instance when it truly loses focus.
      this.close(false);
    }, 160);
  }

  onKeyup(event) {
    // Keep the “popular/recent” empty state open; only close on explicit reset/blur.
    if (!this.getQuery().length) return;

    const code = event.code;
    if (code !== 'ArrowUp' && code !== 'ArrowDown' && code !== 'Enter') return;

    if (code === 'ArrowUp' || code === 'ArrowDown') {
      event.preventDefault();
      this.switchOption(code === 'ArrowUp' ? 'up' : 'down');
      return;
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) {
      event.preventDefault();
      this.selectOption();
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
      return;
    }

    if (event.code === 'Escape') {
      if (this.hasAttribute('open')) {
        event.preventDefault();
        this.close(false);
      }
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const el = this.querySelector('[data-predictive-search-search-for-text]');
    const current = el?.innerText;
    if (!el || !current) return;

    if (!previousTerm) {
      const inQuotes = current.replace(/(["“”])([^"“”]*)(["“”])/, `$1${newTerm}$3`);
      el.innerText = inQuotes !== current ? inQuotes : `${current.trim()} ${newTerm}`;
      return;
    }

    const safePrev = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const matches = current.match(new RegExp(safePrev, 'g'));
    if (matches && matches.length > 1) return;

    el.innerText = current.replace(previousTerm, newTerm);
  }

  switchOption(direction) {
    if (!this.hasAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = this.getSelectableOptions();
    if (!allVisibleElements.length) return;

    if (moveUp && !selectedElement) return;

    const selectedIndex = allVisibleElements.indexOf(selectedElement);

    if (this.statusElement) this.statusElement.textContent = '';

    let activeIndex = 0;
    if (!moveUp && selectedElement) {
      activeIndex = selectedIndex === allVisibleElements.length - 1 ? 0 : selectedIndex + 1;
    } else if (moveUp) {
      activeIndex = selectedIndex <= 0 ? allVisibleElements.length - 1 : selectedIndex - 1;
    }

    if (activeIndex === selectedIndex) return;

    const activeElement = allVisibleElements[activeIndex];
    if (!activeElement.id) activeElement.id = `predictive-option-${activeIndex}-${Date.now()}`;

    activeElement.setAttribute('aria-selected', 'true');
    if (selectedElement) selectedElement.setAttribute('aria-selected', 'false');

    this.input.setAttribute('aria-activedescendant', activeElement.id);

    if (typeof activeElement.scrollIntoView === 'function') {
      activeElement.scrollIntoView({ block: 'nearest' });
    }
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"], a[aria-selected="true"]'
    );
    if (selectedOption) selectedOption.click();
  }

  /* -----------------------------
     Conversion mode: popular + recent searches (empty query)
  ------------------------------ */

  escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  getPopularTerms() {
    try {
      const raw = this.getAttribute('data-cbh-popular-searches');
      if (!raw) return this._popularFallback || [];
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.filter(Boolean).slice(0, 12);
    } catch (e) {}
    return this._popularFallback || [];
  }

  readRecentTerms() {
    try {
      if (!window.localStorage) return [];
      const raw = localStorage.getItem(this._recentKey);
      const arr = JSON.parse(raw || '[]');
      if (Array.isArray(arr)) return arr.filter(Boolean).slice(0, this._recentMax || 6);
    } catch (e) {}
    return [];
  }

  writeRecentTerms(arr) {
    try {
      if (!window.localStorage) return;
      localStorage.setItem(this._recentKey, JSON.stringify(arr || []));
    } catch (e) {}
  }

  storeRecentTerm(term) {
    const t = (term || '').trim();
    if (!t) return;

    const norm = this.normaliseTerm(t);
    if (!norm) return;

    const existing = this.readRecentTerms().filter((x) => this.normaliseTerm(x) !== norm);
    existing.unshift(t);
    this.writeRecentTerms(existing.slice(0, this._recentMax || 6));
  }

  removeEmptyState() {
    if (!this.predictiveSearchResults) return;
    const node = this.predictiveSearchResults.querySelector('[data-cbh-predictive-empty]');
    if (node) node.remove();
  }

  ensurePanelHasSpinner() {
    if (!this.predictiveSearchResults) return;

    const isEmpty = !!this.predictiveSearchResults.querySelector('[data-cbh-predictive-empty]');
    if (!isEmpty) return;

    // Restore initial spinner markup so loading state isn't blank
    this.predictiveSearchResults.innerHTML = this._initialPanelMarkup || '';
  }

  renderEmptyState() {
    if (!this.predictiveSearchResults) return;

    // If results are currently loading, don't stomp them
    if (this.hasAttribute('loading')) return;

    const popular = this.getPopularTerms();
    const recent = this.readRecentTerms();

    const chips = (label, items) => {
      if (!items || !items.length) return '';
      const btns = items
        .slice(0, 12)
        .map((t) => {
          const safe = this.escapeHtml(t);
          return `<button type="button" class="cbh-predictive-chip" data-cbh-predictive-chip="${safe}">${safe}</button>`;
        })
        .join('');
      return `
        <div class="cbh-predictive-empty__section">
          <p class="cbh-predictive-empty__title">${this.escapeHtml(label)}</p>
          <div class="cbh-predictive-chips" role="list">${btns}</div>
        </div>
      `;
    };

    const html = `
      <div class="cbh-predictive-empty" data-cbh-predictive-empty>
        ${chips('Popular searches', popular)}
        ${chips('Recent searches', recent)}
        <p class="cbh-predictive-empty__hint">Tip: try a brand name, product type, or code.</p>
      </div>
    `;

    this.predictiveSearchResults.innerHTML = html;
  }

  onResultsClick(event) {
    const t = event.target;
    if (!t || t.nodeType !== 1) return;

    const chip = t.closest('[data-cbh-predictive-chip]');
    if (chip) {
      event.preventDefault();
      const term = (chip.getAttribute('data-cbh-predictive-chip') || chip.textContent || '').trim();
      if (!term) return;

      this.input.value = term;
      this.storeRecentTerm(term);

      // Trigger the normal fetch path
      this.onChange();
      this.open();
      return;
    }

    // Any click on a result counts as “intent”: store the current term
    const link = t.closest('a');
    if (link) {
      const term = this.getQuery();
      if (term.length) this.storeRecentTerm(term);
    }
  }

  applyHighlights(term) {
    const q = (term || '').trim();
    if (!q) return;

    let re = null;
    try {
      const safe = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      re = new RegExp(safe, 'ig');
    } catch (e) {
      return;
    }

    const headings = Array.from(this.querySelectorAll('.predictive-search__item-heading'));
    headings.forEach((el) => {
      if (!el) return;

      // Skip headings that already contain markup (Shopify uses styled_text for query suggestions)
      if (el.children && el.children.length) return;
      if (el.querySelector && el.querySelector('mark')) return;

      const txt = el.textContent || '';
      if (!txt) return;
      if (!re.test(txt)) return;

      el.innerHTML = this.escapeHtml(txt).replace(re, (m) => `<mark>${this.escapeHtml(m)}</mark>`);
    });
  }

  abortInFlight() {
    try {
      if (this.abortController) this.abortController.abort();
    } catch (err) {}
    this.abortController = null;
  }

  getSearchResults(searchTerm) {
    const key = this.getCacheKey(searchTerm);
    this.setLiveRegionLoadingState();

    if (this.cachedResults[key]) {
      this.renderSearchResults(this.cachedResults[key], { fromCache: true });
      return;
    }

    this.abortInFlight();
    const controller = new AbortController();
    this.abortController = controller;

    const reqId = ++this._requestSeq;

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: controller.signal,
    })
      .then((response) => {
        if (reqId !== this._requestSeq || this.abortController !== controller) return null;

        if (!response.ok) {
          this.close(false);
          throw new Error(String(response.status));
        }
        return response.text();
      })
      .then((text) => {
        if (!text) return;
        if (reqId !== this._requestSeq || this.abortController !== controller || controller.signal.aborted) return;

        const resultsMarkup =
          new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#shopify-section-predictive-search')?.innerHTML || '';

        // Share cache across instances (defensive)
        this.getAllInstances().forEach((instance) => {
          try {
            if (instance && instance.cachedResults) instance.cachedResults[key] = resultsMarkup;
          } catch (e) {}
        });

        if (this.getQuery() !== searchTerm) return;

        this.renderSearchResults(resultsMarkup, { fromCache: false });
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) return;
        this.close(false);
      })
      .finally(() => {
        if (this.abortController === controller) this.abortController = null;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    if (!this.statusElement) return;

    this.setLiveRegionText(this.loadingText || '');
    this.setAttribute('loading', 'true');
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup, { fromCache = false } = {}) {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.innerHTML = resultsMarkup;

    // Normalise results attr: present means "we have results ready"
    this.setAttribute('results', 'true');

    const options = this.getSelectableOptions();
    options.forEach((el, idx) => {
      if (!el.id) el.id = `predictive-option-${idx}-${Date.now()}`;
    });

    this.setLiveRegionResults();
    this.clearInlinePositioning();
    this.applyHighlights(this.getQuery());

    const shouldAutoOpen = document.activeElement === this.input || this.hasAttribute('open');

    if (shouldAutoOpen) {
      this.open();
    } else {
      // keep results ready but closed
      this.close(false);
    }
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countEl) this.setLiveRegionText(countEl.textContent);
  }

  open() {
    if (!this.predictiveSearchResults) return;

    this.clearInlinePositioning();

    this.setAttribute('open', 'true');
    if (this.predictiveSearchResults) this.predictiveSearchResults.setAttribute('aria-hidden', 'false');
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
    // header-interactions.js will manage global classes
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.abortInFlight();
      // If we clear, we also clear “results” state
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', 'false');

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    if (this.predictiveSearchResults) this.predictiveSearchResults.setAttribute('aria-hidden', 'true');

    // IMPORTANT: do NOT remove `results` on a normal close
    // (keeps “ready but closed” behaviour for onFocus -> open)
    this.input.setAttribute('aria-expanded', 'false');

    this.isOpen = false;
    this.toggleResetButton();
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
